/**
 * External dependencies
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

/**
 * Internal dependencies
 */
import { requestCurrentUniversalImporterIfNotRequested } from 'state/current-universal-importer/actions';
import { getCurrentUniversalImporter, requestingCurrentUniversalImporter } from 'state/current-universal-importer/selectors';
import UniversalImporterPlanLoader from './plan-loader';
import Money from 'components/money';
import PlanName from 'components/plan-name';
import DateTime from 'components/date-time';
import Coupon from 'components/coupon';
import { Stack, Box, Text } from '@towersystems/towersystems-design-system';
import DetailListItem from 'components/detail-list-item';
import SubscriptionStatus from 'components/subscription-status';
import Card from 'components/card';

/**
 * Style dependencies
 */
import './styles.scss';

const CurrentPlanCard = (props) => {
    const { currentUniversalImporter, requesting } = props;

    useEffect(() => {
        props.requestCurrentUniversalImporter();
    }, []);

    if (!currentUniversalImporter && requesting) {
        return <UniversalImporterPlanLoader />;
    }

    if (!currentUniversalImporter || currentUniversalImporter.length === 0) {
        return (
            <Card>
                <Card.Header heading='Plan details' />
                <Stack>
                    <Card.Section>
                        <DetailListItem label='Plan amount' />
                    </Card.Section>
                    <Card.Section>
                        <DetailListItem label='Billing period' />
                    </Card.Section>
                    <Card.Section>
                        <DetailListItem label='Next billing date' />
                    </Card.Section>
                </Stack>
            </Card>
        );
    }

    return (
        <Stack space='large'>
            {currentUniversalImporter.map((item) => {
                const s = item.subscription;
                return (
                    <Card key={s.id}>
                        <Card.Header heading={<PlanName value={s.plan_id} />} />
                        <Stack dividers>
                            <Card.Section>
                                <DetailListItem
                                    label='Status'
                                    detail={<SubscriptionStatus status={s.status} />}
                                />
                            </Card.Section>
                            <Card.Section>
                                <DetailListItem
                                    label='Price'
                                    detail={
                                        <span>
                                            <Money value={s.plan_unit_price / 100} /> / {s.billing_period} {s.billing_period_unit}
                                        </span>
                                    }
                                />
                            </Card.Section>
                            {s.status === 'active' && (
                                <Card.Section>
                                    <DetailListItem
                                        label='Next billing date'
                                        detail={<DateTime date={s.next_billing_at} format='Do MMM, YYYY' />}
                                    />
                                </Card.Section>
                            )}
                            {s.status === 'future' && (
                                <Card.Section>
                                    <DetailListItem
                                        label='Start date'
                                        detail={<DateTime date={s.start_date} format='Do MMM, YYYY' />}
                                    />
                                </Card.Section>
                            )}
                            {s.addons && s.addons.length > 0 && (
                                <Card.Section>
                                    <DetailListItem
                                        label='Add-ons'
                                        detail={
                                            <Stack space='small'>
                                                {s.addons.map(({ id, amount, quantity }) => (
                                                    <Text key={id} size='small'>
                                                        {id} — <Money value={amount / 100} /> (×{quantity})
                                                    </Text>
                                                ))}
                                            </Stack>
                                        }
                                    />
                                </Card.Section>
                            )}
                            {s.coupons && s.coupons.length > 0 && (
                                <Card.Section>
                                    <DetailListItem
                                        label='Discounts'
                                        detail={
                                            <Stack space='small'>
                                                {s.coupons.map(({ coupon_id }) => (
                                                    <Coupon key={coupon_id} couponId={coupon_id} />
                                                ))}
                                            </Stack>
                                        }
                                    />
                                </Card.Section>
                            )}
                        </Stack>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default connect(
    (state) => ({
        currentUniversalImporter: getCurrentUniversalImporter(state),
        requesting: requestingCurrentUniversalImporter(state),
    }),
    {
        requestCurrentUniversalImporter: requestCurrentUniversalImporterIfNotRequested,
    },
)(CurrentPlanCard);
